import { useSyncExternalStore } from "react";
import { HGIntervalType } from "@/common/types";
import { SpanningMode } from "@/common/recordTree";

const STORAGE_KEY = "eventera.recordsView";

export type RecordsMode = "time" | "notes";
export type SortOrder = "asc" | "desc";
export type NotesSortField = "created" | "modified";
/** How far down a collection's subtree the record list reaches. */
export type CollectionScope = "direct" | "children" | "all";

export interface RecordsViewOptions {
  mode: RecordsMode;
  /** Interval type for the first level below the active interval. */
  groupBy: "auto" | HGIntervalType;
  /** Interval type for the level below that, or "none" to stop. */
  thenBy: "auto" | "none" | HGIntervalType;
  order: SortOrder;
  notesSort: NotesSortField;
  notesOrder: SortOrder;
  /** Where a record crossing several sub-intervals is listed. */
  spanning: SpanningMode;
  showGaps: boolean;
  /**
   * On a collection page, which records the list reaches: only the collection's
   * own ("direct"), plus its immediate child collections ("children"), or the
   * whole subtree ("all"). Ignored outside a collection.
   */
  collectionScope: CollectionScope;
}

export const defaultRecordsViewOptions: RecordsViewOptions = {
  mode: "time",
  groupBy: "auto",
  thenBy: "auto",
  order: "asc",
  notesSort: "created",
  notesOrder: "desc",
  spanning: "ends",
  showGaps: true,
  collectionScope: "direct",
};

function readStored(): RecordsViewOptions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultRecordsViewOptions;
    // Merged over the defaults so options added later never break a stored view.
    return { ...defaultRecordsViewOptions, ...JSON.parse(raw) };
  } catch {
    return defaultRecordsViewOptions;
  }
}

/**
 * Toolbar state for the Records view, persisted to localStorage.
 *
 * A store rather than component state because the mode is no longer set only by
 * the toolbar: opening a collection with nothing on the time axis switches to
 * notes, and so does pressing the already-open collection in the sidebar — both
 * from outside the Records tree. Deliberately still not in Redux: it has to
 * survive a reload, which Redux state here does not.
 */
let options: RecordsViewOptions = readStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRecordsViewOptions() {
  return options;
}

export function setRecordsViewOption<K extends keyof RecordsViewOptions>(
  key: K,
  value: RecordsViewOptions[K],
) {
  // Bailing out on an unchanged value keeps the object identity stable, which
  // the record tree memo in Records depends on.
  if (options[key] === value) return;
  options = { ...options, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch {
    // A full or disabled storage costs the user persistence, not the view.
  }
  listeners.forEach((listener) => listener());
}

export function useRecordsViewOptions() {
  return {
    options: useSyncExternalStore(subscribe, getRecordsViewOptions),
    setOption: setRecordsViewOption,
  };
}
